<template>
    <div>
        <h5>Aktiva fält <i class="fal fa-square-check active-color"></i></h5>
        <SbTableLite
            :fields="fields"
            :items="selectedFieldValues"
            :busy="loadingTables"
            striped
            hover
            small
            responsive
        >
            <template #table-busy>
                <div class="text-center text-danger my-2">
                    <SbSpinner class="align-middle"></SbSpinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(links)="row">
                <div class="d-flex" style="gap: 10px">
                    <a
                        v-if="row.index < values.length - 1"
                        @click="handleMoveDown(row.index)"
                        type="button"
                        ><i class="fal fa-arrow-down"></i
                    ></a>
                    <a
                        v-if="row.index > 0"
                        @click="handleMoveUp(row.index)"
                        type="button"
                        ><i class="fal fa-arrow-up"></i
                    ></a>
                    <a size="sm" @click="row.toggleDetails" type="button"
                        ><i class="fal fa-edit"></i
                    ></a>
                    <a
                        size="sm"
                        @click="moveToAvailable(row.index)"
                        type="button"
                        ><i class="fal fa-minus"></i
                    ></a>
                </div>
            </template>
            <template #cell(settings)="row">
                <div class="d-flex" style="gap: 10px">
                    <i
                        v-bind:class="{
                            'active-color': !row.item.hideForContact,
                        }"
                        class="fal fa-eye"
                    ></i>
                    <i
                        v-bind:class="{ 'active-color': row.item.required }"
                        class="fal fa-octagon-exclamation"
                    ></i>
                    <i
                        v-bind:class="{
                            'active-color': row.item.editableByContact,
                        }"
                        class="fal fa-pencil"
                    ></i>
                    <i
                        v-bind:class="{
                            'active-color': row.item.sensitive,
                        }"
                        class="fal fa-user-shield"
                    ></i>
                </div>
            </template>
            <template #row-details="row">
                <quote-field
                    :key="row.id"
                    :inputTypes="inputTypes"
                    :attribute="row.item"
                    @submitted="(payload) => handleFieldEdit(row, payload)"
                    @cancelled="row.toggleDetails"
                />
            </template>
        </SbTableLite>

        <hr />

        <h5>Inaktiva fält <i class="fal fa-square-check"></i></h5>
        <SbTableLite
            :fields="fields"
            :items="availableFieldValues"
            :busy="loadingTables"
            striped
            hover
            small
            responsive
        >
            <template #table-busy>
                <div class="text-center text-danger my-2">
                    <SbSpinner class="align-middle"></SbSpinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(links)="row">
                <div class="d-flex" style="gap: 10px">
                    <a
                        size="sm"
                        @click="moveToSelected(row.index)"
                        type="button"
                        ><i class="fal fa-plus"></i
                    ></a>
                </div>
            </template>
            <template #cell(settings)="row">
                <div class="d-flex" style="gap: 10px">
                    <i
                        v-bind:class="{
                            'active-color': !row.item.hideForContact,
                        }"
                        class="fal fa-eye"
                    ></i>
                    <i
                        v-bind:class="{ 'active-color': row.item.required }"
                        class="fal fa-octagon-exclamation"
                    ></i>
                    <i
                        v-bind:class="{
                            'active-color': row.item.editableByContact,
                        }"
                        class="fal fa-pencil"
                    ></i>
                    <i
                        v-bind:class="{
                            'active-color': row.item.sensitive,
                        }"
                        class="fal fa-user-shield"
                    ></i>
                </div>
            </template>
            <template #row-details="row">
                <quote-field
                    :key="row.id"
                    :inputTypes="inputTypes"
                    :attribute="row.item"
                    @submitted="(payload) => handleFieldEdit(row, payload)"
                    @cancelled="row.toggleDetails"
                />
            </template>
        </SbTableLite>

        <div>
            <quote-field
                :key="-1"
                v-if="creating"
                :inputTypes="inputTypes"
                :attribute="customFieldTemplate()"
                @submitted="(payload) => handleFieldCreated(payload)"
                @cancelled="creating = false"
            />
            <div class="mt-3">
                <form
                    id="sync-with-unsigned-quotes-form"
                    @submit.prevent="submit"
                >
                    <SbFormGroup class="col-6">
                        <SbFormCheckbox
                            id="sync-with-unsigned-quotes"
                            v-model="syncWithUnsignedQuotes"
                            name="sync-with-unsigned-quotes"
                            switch
                        >
                            <label for="sync-with-unsigned-quotes"
                                >Synka med kontaktkort utan konvertering</label
                            >
                        </SbFormCheckbox>
                    </SbFormGroup>
                    <SbFormGroup class="col-6" v-if="syncWithUnsignedQuotes">
                        <label for="start-date">Start Date *</label>
                        <SbFormInput
                            id="start-date"
                            v-model="startDate"
                            type="date"
                            required
                        />
                    </SbFormGroup>
                </form>
            </div>
            <div class="d-flex container-fluid" style="gap: 10px">
                <button
                    class="btn btn-primary"
                    type="submit"
                    form="sync-with-unsigned-quotes-form"
                >
                    <i class="fal fa-save"></i> Spara
                </button>
                <button
                    @click="creating = true"
                    class="btn btn-secondary"
                    type="button"
                >
                    <i class="fal fa-add"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.active-color {
    color: var(--primary-color);
}
</style>
<script setup lang="ts">
import axios from "axios";
import { onMounted, ref } from "vue";
import SbFormCheckbox from "../sb/SbFormCheckbox.vue";
import SbFormGroup from "../sb/SbFormGroup.vue";
import SbFormInput from "../sb/SbFormInput.vue";
import SbSpinner from "../sb/SbSpinner.vue";
import SbTableLite from "../sb/SbTableLite.vue";

type FieldValue = Record<string, any> & { enabled: boolean };
type TableRow = { index: number; toggleDetails: () => void };
interface Props { quoteProfileId: number; definitions?: FieldValue[]; inputTypes?: unknown[]; values?: FieldValue[]; }
const props = withDefaults(defineProps<Props>(), { definitions: () => [], values: () => [] });
const fields = [
    { key: "links", label: "" }, { key: "label", label: "Fältnamn" },
    { key: "placeholder", label: "Visningstext" }, { key: "settings", label: "Inställningar" },
    { key: "name", label: "Systemnamn" }, { key: "fieldType", label: "Fälttyp" }, { key: "type", label: "Typ" },
];
const creating = ref(false);
const updating = ref(false);
const loadingTables = ref(false);
const selectedFieldValues = ref<FieldValue[]>([]);
const availableFieldValues = ref<FieldValue[]>([]);
const syncWithUnsignedQuotes = ref(false);
const startDate = ref<string | null>(null);
function moveToSelected(index: number): void { const value = availableFieldValues.value.splice(index, 1)[0]; value.enabled = true; selectedFieldValues.value.push(value); }
function moveToAvailable(index: number): void { const value = selectedFieldValues.value.splice(index, 1)[0]; value.enabled = false; availableFieldValues.value.push(value); }
function handleFieldEdit(row: TableRow, payload: FieldValue): void { selectedFieldValues.value[row.index] = payload; row.toggleDetails(); if (!payload.enabled) moveToAvailable(row.index); }
function handleFieldCreated(payload: FieldValue): void { availableFieldValues.value.push(payload); creating.value = false; }
function swapValues(i: number, j: number): void { selectedFieldValues.value[j] = selectedFieldValues.value.splice(i, 1, selectedFieldValues.value[j])[0]; }
function handleMoveUp(index: number): void { if (index >= 1) swapValues(index - 1, index); }
function handleMoveDown(index: number): void { if (index < props.values.length) swapValues(index + 1, index); }
function customFieldTemplate(): FieldValue { return { name: "", label: "", type: null, fieldType: "custom", required: false, enabled: false, placeholder: "", hideForContact: false, editableByContact: false, sensitive: false }; }
async function submit(): Promise<void> {
    try {
        updating.value = true;
        await axios.post(route("quoteProfiles.updateFieldAttributes", props.quoteProfileId), {
            fields: [...selectedFieldValues.value, ...availableFieldValues.value],
            syncWithUnsignedQuotes: syncWithUnsignedQuotes.value,
            startDate: startDate.value,
        });
        window.location.href = window.location.href;
    } catch (error) {
        console.log(error); updating.value = false;
    }
}
onMounted(() => {
    loadingTables.value = true;
    selectedFieldValues.value = props.values.length ? props.values.filter((value) => value.enabled) : [];
    availableFieldValues.value = props.values.length ? props.values.filter((value) => !value.enabled) : [...props.definitions];
    loadingTables.value = false;
});
</script>
